import {
  SCALE_INTERVALS,
  OPEN_STRINGS,
  getNoteAtPosition,
  noteToSemitones,
  semitonesToNote,
  shouldUseFlats,
  type NoteName,
} from "./guitarEngine";

export interface FingeredNote {
  string: number;      // 6 (grave) a 1 (aguda)
  fret: number;
  note: NoteName;
  finger: number;      // 0=al aire/cejilla, 1=índice, 2=medio, 3=anular, 4=meñique
  degree: string;      // R, 2, b3, 3, 4, #4, 5, b6, 6, b7, 7
}

export interface FretboardPattern {
  type: 'fingering' | 'caged';
  id: string;               // identificador único, ej: "fingering-5" o "caged-1"
  label: string;            // etiqueta visible, ej: "5" o "E"
  position: number;         // traste base / traste más bajo
  notes: FingeredNote[];
  span: number;             // maxFret - minFret de las notas usadas
  comfortScore?: number;    // solo fingering
  score?: number;           // solo caged
  stringRange: [number, number]; // [grave, aguda]
  coverage: number;         // cuántos grados únicos cubre
  lowestFret: number;
  highestFret: number;
}

/** @deprecated Use FretboardPattern instead. Este alias se eliminará en v3. */
export type ScaleFingering = FretboardPattern;

const DEGREE_MAP: Record<string, string[]> = {
  major: ["R", "2", "3", "4", "5", "6", "7"],
  minor: ["R", "2", "b3", "4", "5", "b6", "b7"],
  minor_harmonic: ["R", "2", "b3", "4", "5", "b6", "7"],
  minor_melodic: ["R", "2", "b3", "4", "5", "6", "7"],
  pentatonic_major: ["R", "2", "3", "5", "6"],
  pentatonic_minor: ["R", "b3", "4", "5", "b7"],
  blues: ["R", "b3", "4", "#4", "5", "b7"],
  chromatic: ["1", "b2", "2", "b3", "3", "4", "#4", "5", "b6", "6", "b7", "7"],
};

function getDegreeNames(scaleType: string): string[] {
  return DEGREE_MAP[scaleType] || DEGREE_MAP.major;
}

function assignFinger(fret: number, position: number): number {
  const offset = fret - position;
  if (offset <= -1) return 1;   // stretch back con índice
  if (offset === 0) return 1;   // índice en posición base
  if (offset === 1) return 2;   // medio
  if (offset === 2) return 3;   // anular
  if (offset === 3) return 4;   // meñique
  if (offset === 4) return 4;   // stretch forward con meñique
  return 0;                     // fuera de alcance o cejilla/al aire
}

function calculateComfort(notes: FingeredNote[], position: number): number {
  let comfort = 100;

  // Penalizar stretches entre dedos consecutivos en misma cuerda
  const byString = new Map<number, FingeredNote[]>();
  for (const n of notes) {
    const arr = byString.get(n.string) || [];
    arr.push(n);
    byString.set(n.string, arr);
  }
  for (const arr of byString.values()) {
    if (arr.length < 2) continue;
    arr.sort((a, b) => a.fret - b.fret);
    for (let i = 0; i < arr.length - 1; i++) {
      const diff = arr[i + 1].fret - arr[i].fret;
      if (diff > 2) comfort -= 8;
      if (diff > 3) comfort -= 12;
    }
  }

  // Penalizar uso excesivo de meñique
  const pinkyCount = notes.filter(n => n.finger === 4).length;
  comfort -= pinkyCount * 5;

  // Penalizar stretches del índice hacia atrás
  const indexStretchBack = notes.filter(n => n.finger === 1 && n.fret < position).length;
  comfort -= indexStretchBack * 4;

  // Penalizar trastes muy altos
  const avgFret = notes.reduce((a, n) => a + n.fret, 0) / notes.length;
  if (avgFret > 12) comfort -= 10;
  else if (avgFret > 9) comfort -= 5;

  // Bonificar posiciones abiertas/bajas
  if (avgFret <= 5) comfort += 8;

  // Penalizar si no cubre todos los grados
  const uniqueDegrees = new Set(notes.map(n => n.degree)).size;
  const expected = getDegreeNames("major").length;
  if (uniqueDegrees < expected) comfort -= (expected - uniqueDegrees) * 10;

  return Math.max(0, comfort);
}

/**
 * Genera patrones CAGED reales usando ventana deslizante.
 * Para cada posición base, toma TODAS las notas de la escala que caen
 * dentro de la ventana de mano [base, base+4], generando una "caja" contigua.
 */
export function generateScaleFingerings(
  root: NoteName,
  scaleType: string,
  maxFret: number = 15
): FretboardPattern[] {
  const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
  const useFlats = shouldUseFlats(root, scaleType);
  const rootSemi = noteToSemitones(root);
  const degreeNames = getDegreeNames(scaleType);

  // Notas de la escala con sus grados
  const scaleDegrees = intervals.map((interval, idx) => ({
    semitone: (rootSemi + interval) % 12,
    degree: degreeNames[idx] || String(idx + 1),
  }));

  const results: FretboardPattern[] = [];

  // Ventana deslizante: para cada posición base posible
  for (let pos = 0; pos <= maxFret - 2; pos++) {
    const windowStart = Math.max(0, pos);
    const windowEnd = Math.min(maxFret, pos + 4);

    const allNotes: FingeredNote[] = [];
    const degreesInPattern = new Set<string>();

    // Para cada cuerda, tomar TODAS las notas de la escala dentro de la ventana
    for (let s = 6; s >= 1; s--) {
      const stringNotes: FingeredNote[] = [];

      for (let f = windowStart; f <= windowEnd; f++) {
        const posInfo = getNoteAtPosition(s, f);
        const posSemi = noteToSemitones(posInfo.note);
        const matched = scaleDegrees.find(sd => sd.semitone === posSemi);
        if (matched) {
          const isOpen = f === 0 && posInfo.note === OPEN_STRINGS[6 - s].note;
          const finger = isOpen ? 0 : assignFinger(f, pos);
          stringNotes.push({
            string: s,
            fret: f,
            note: semitonesToNote(posSemi, useFlats),
            finger,
            degree: matched.degree,
          });
        }
      }

      // Si una cuerda tiene más de 3 notas en la ventana, tomar las 3 más bajas
      // (esto raramente pasa, pero por seguridad)
      const limited = stringNotes.slice(0, 3);
      for (const n of limited) {
        allNotes.push(n);
        degreesInPattern.add(n.degree);
      }
    }

    if (allNotes.length === 0) continue;

    // Calcular span real
    const frets = allNotes.map(n => n.fret);
    const minFretUsed = Math.min(...frets);
    const maxFretUsed = Math.max(...frets);
    const span = maxFretUsed - minFretUsed;

    // Validaciones
    if (span > 5) continue;

    // Debe tener notas en al menos 4 cuerdas consecutivas
    const stringsUsed = [...new Set(allNotes.map(n => n.string))].sort((a, b) => b - a);
    let consecutiveStrings = 1;
    let maxConsecutive = 1;
    for (let i = 1; i < stringsUsed.length; i++) {
      if (stringsUsed[i - 1] - stringsUsed[i] === 1) {
        consecutiveStrings++;
        maxConsecutive = Math.max(maxConsecutive, consecutiveStrings);
      } else {
        consecutiveStrings = 1;
      }
    }
    if (maxConsecutive < 4 && stringsUsed.length < 4) continue;

    // Verificar que no haya saltos de cuerda > 2 entre cuerdas consecutivas usadas
    let validStrings = true;
    for (let i = 1; i < stringsUsed.length; i++) {
      if (stringsUsed[i - 1] - stringsUsed[i] > 2) {
        validStrings = false;
        break;
      }
    }
    if (!validStrings) continue;

    const comfort = calculateComfort(allNotes, pos);

    // Ordenar de grave a aguda para presentación consistente
    const sorted = [...allNotes].sort((a, b) => b.string - a.string);

    results.push({
      type: 'fingering',
      id: `fingering-${pos}`,
      label: String(pos),
      position: pos,
      notes: sorted,
      span,
      comfortScore: comfort,
      stringRange: [Math.max(...stringsUsed), Math.min(...stringsUsed)] as [number, number],
      coverage: degreesInPattern.size,
      lowestFret: minFretUsed,
      highestFret: maxFretUsed,
    });
  }

  // Ordenar por comfort descendente, luego por posición ascendente
  return results.sort((a, b) => {
    if (b.comfortScore !== a.comfortScore) return b.comfortScore - a.comfortScore;
    return a.position - b.position;
  });
}

/**
 * Genera una secuencia que conecta múltiples cajas CAGED.
 * Recorre cada posición ordenada por traste base, y dentro de cada una
 * recorre las notas en orden musical (grave → aguda).
 * Ideal para ejercicios de conexión entre posiciones.
 */
export function buildBoxConnectionSequence(
  fingerings: FretboardPattern[],
  options: {
    direction?: "asc" | "desc" | "both";
    octaves?: number;
  } = {}
): import("./guitarEngine").NeckPosition[] {
  const { direction = "asc", octaves = 1 } = options;
  if (fingerings.length === 0) return [];

  // Ordenar posiciones de izquierda a derecha (grave → aguda en el mástil)
  const sortedPositions = [...fingerings].sort((a, b) => a.position - b.position);

  type NeckPos = import("./guitarEngine").NeckPosition;
  const result: NeckPos[] = [];

  for (let oct = 0; oct < octaves; oct++) {
    for (const f of sortedPositions) {
      // Ordenar notas de la caja por pitch MIDI (grave → aguda)
      const notes = [...f.notes].sort((a, b) => {
        const midiA = OPEN_STRINGS[6 - a.string].midi + a.fret;
        const midiB = OPEN_STRINGS[6 - b.string].midi + b.fret;
        return midiA - midiB;
      });

      let ordered: typeof notes = [];
      if (direction === "asc") {
        ordered = notes;
      } else if (direction === "desc") {
        ordered = [...notes].reverse();
      } else {
        // both: sube y baja sin repetir extremos
        ordered = [...notes, ...[...notes].reverse().slice(1, -1)];
      }

      for (const n of ordered) {
        result.push({
          string: n.string,
          fret: n.fret,
          note: n.note,
          degree: n.degree as any,
          highlight: n.degree === "R" ? "root" : n.degree === "3" || n.degree === "b3" ? "third" : n.degree === "5" ? "fifth" : "scale",
          finger: n.finger,
        });
      }
    }
  }

  return result;
}

/**
 * Convierte un FretboardPattern a NeckPosition[] para ser consumido por GuitarNeck.
 */
export function fingeringToNeckPositions(
  fingering: FretboardPattern,
  highlight: "root" | "scale" = "scale"
): import("./guitarEngine").NeckPosition[] {
  return fingering.notes.map(n => ({
    string: n.string,
    fret: n.fret,
    note: n.note,
    degree: n.degree as any,
    highlight: n.degree === "R"
      ? "root"
      : n.degree === "3" || n.degree === "b3"
        ? "third"
        : n.degree === "5"
          ? "fifth"
          : highlight,
    finger: n.finger,
  }));
}
